/**
 * Le chiamate della sezione Progetti, in un posto solo.
 *
 * Una sezione sola al posto di tre pagine di divisione: la divisione è
 * diventata un filtro, quindi qui dentro non c'è nessuna query "per divisione",
 * c'è una query con un parametro.
 */
import { keepPreviousData } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { ModuleKey, PermissionLevel, hasAtLeast, type Division, type ProjectStatus } from '@masamune/shared';

// ── Le righe ───────────────────────────────────────────────────────────────

export interface ProjectRow {
  id: string;
  code: string;
  name: string;
  client: { id: string; legalName: string; shortName: string };
  /** Da dove nasce: null quando il progetto non viene da nessuna trattativa. */
  deal: {
    id: string;
    title: string;
    amount: number;
    expectedCloseDate: string | null;
    stage: { name: string; outcome: string } | null;
  } | null;
  division: Division;
  model: string;
  status: ProjectStatus;
  phase: string;
  progress: number;
  pm: { id: string; firstName: string; lastName: string; initials: string } | null;
  startDate: string | null;
  deadline: string | null;
  endDate: string | null;
  /** La consegna è passata e il progetto non è chiuso. Lo calcola il server. */
  isLate: boolean;
  giorniRitardo: number;
  /** La consegna che ci siamo dati è più in là di quella promessa in trattativa. */
  oltrePromessa: boolean;
  revenue: number;
  cost?: number;
  marginPct?: number;
  riskLevel: string;
  tasksCount: number;
}

export interface ProjectsPage {
  items: ProjectRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** I numeri in testa alla sezione: li calcola il server sullo stesso filtro. */
export interface ProjectsSummary {
  totale: number;
  vivi: number;
  inRitardo: number;
  inConsegna30: number;
  senzaScadenza: number;
  valoreImpegnato: number;
}

export interface DivisionCount {
  division: Division;
  count: number;
}

/** Una consegna di Design. `project` è null quando non è agganciata a nessuno. */
export interface DeliverableRow {
  id: string;
  title: string;
  description: string | null;
  type: string;
  status: string;
  revisions: number;
  deadline: string | null;
  figmaUrl: string | null;
  owner: { id: string; firstName: string; lastName: string; initials: string } | null;
  project: { id: string; code: string; name: string } | null;
  updatedAt: string;
}

// ── I filtri, in un oggetto solo ───────────────────────────────────────────

export interface ProjectFilters {
  division: string;
  status: string;
  clientId: string;
  pmId: string;
  search: string;
  /** Solo quelli che hanno sforato la consegna. */
  late: boolean;
}

/**
 * I filtri in query-string. Si scrive solo quello che è acceso: un URL con
 * `?division=&status=&late=false` non si legge e non si incolla a nessuno.
 */
function toParams(f: ProjectFilters, extra: Record<string, string | number> = {}): string {
  const sp = new URLSearchParams();
  if (f.division) sp.set('division', f.division);
  if (f.status) sp.set('status', f.status);
  if (f.clientId) sp.set('clientId', f.clientId);
  if (f.pmId) sp.set('pmId', f.pmId);
  if (f.search.trim()) sp.set('search', f.search.trim());
  if (f.late) sp.set('late', 'true');
  for (const [k, v] of Object.entries(extra)) sp.set(k, String(v));
  return sp.toString();
}

/** Quanti filtri sono accesi: è il pallino sul tasto "Filtri" del telefono. */
export function quantiFiltri(f: ProjectFilters): number {
  return [f.status, f.clientId, f.pmId, f.search.trim(), f.late ? '1' : ''].filter(Boolean).length;
}

export const VUOTI: ProjectFilters = { division: '', status: '', clientId: '', pmId: '', search: '', late: false };

// ── Le query ───────────────────────────────────────────────────────────────

export const projectsListQuery = (f: ProjectFilters, page: number, pageSize = 25) => ({
  queryKey: ['projects', 'list', f, page, pageSize] as const,
  queryFn: () => api.get<ProjectsPage>(`/projects?${toParams(f, { page, pageSize })}`),
  placeholderData: keepPreviousData,
});

/**
 * La bacheca carica tutto l'insieme filtrato in una volta: le colonne sono gli
 * stati, e una colonna paginata non è una colonna. Il tetto di 200 è quello
 * dell'API; oggi i progetti sono zero, e quando saranno tanti la lista resta
 * la vista che pagina davvero.
 */
export const projectsBoardQuery = (f: ProjectFilters) => ({
  queryKey: ['projects', 'board', f] as const,
  queryFn: () => api.get<ProjectsPage>(`/projects?${toParams(f, { pageSize: 200 })}`),
  placeholderData: keepPreviousData,
});

export const projectsSummaryQuery = (f: ProjectFilters) => ({
  queryKey: ['projects', 'summary', f] as const,
  queryFn: () => api.get<ProjectsSummary>(`/projects/summary?${toParams(f)}`),
  placeholderData: keepPreviousData,
});

/** I conteggi per divisione ignorano il filtro divisione: se no direbbero 0. */
export const projectsDivisionsQuery = (f: ProjectFilters) => ({
  queryKey: ['projects', 'divisions', { ...f, division: '' }] as const,
  queryFn: () => api.get<{ items: DivisionCount[]; totale: number }>(`/projects/divisions?${toParams({ ...f, division: '' })}`),
  placeholderData: keepPreviousData,
});

/**
 * Le consegne di Design. Si leggono TUTTE, anche quelle senza progetto: sono
 * il motivo per cui questa vista esiste invece di vivere solo dentro la scheda.
 */
export const deliverablesQuery = {
  queryKey: ['deliverables', 'all'] as const,
  queryFn: () => api.get<{ items: DeliverableRow[]; total: number }>('/deliverables?pageSize=200'),
};

/** I PM fra cui scegliere: gli utenti dell'anagrafica interna. */
export const projectPmsQuery = {
  queryKey: ['projects', 'pms'] as const,
  queryFn: () => api.get<{ items: Array<{ id: string; firstName: string; lastName: string; initials: string }> }>('/users?pageSize=100'),
  staleTime: 5 * 60_000,
};

export const projectClientsQuery = {
  queryKey: ['projects', 'clients-lite'] as const,
  queryFn: () => api.get<{ items: Array<{ id: string; shortName: string; legalName: string }> }>('/clients?pageSize=200'),
  staleTime: 5 * 60_000,
};

// ── Permessi ───────────────────────────────────────────────────────────────

/**
 * Chi può aprire un progetto.
 *
 * Si guarda CRM, non MARKETING/PRODUCT/DESIGN, e non è una scorciatoia: è
 * esattamente quello che chiede l'API, dove `POST /projects` è protetto da
 * `requirePermission(CRM, WRITE)`. I tre moduli di divisione servivano a
 * nascondere tre voci di menu che adesso non esistono più.
 */
export function canWriteProjects(user: { permissions: Array<{ module: string; level: string }> } | null): boolean {
  if (!user) return false;
  const lvl = user.permissions.find((p) => p.module === ModuleKey.CRM)?.level ?? PermissionLevel.NONE;
  return hasAtLeast(lvl as PermissionLevel, PermissionLevel.WRITE);
}

/**
 * Chi può vedere le consegne di Design.
 *
 * Qui il modulo è DESIGN e non CRM, perché è quello che chiede l'API: TUTTE le
 * rotte `/deliverables` stanno dietro `requirePermission(DESIGN, READ)`. Chi
 * non ce l'ha — ADMIN_AMM, per esempio, che nel seed non ha DESIGN — si
 * prendeva un 403 che la pagina si mangiava, e la vista diceva "Nessuna
 * consegna". Che è falso: le consegne possono esserci benissimo, è chi guarda
 * che non può vederle. Delle due frasi, quella giusta va detta.
 */
export function canReadDeliverables(user: { permissions: Array<{ module: string; level: string }> } | null): boolean {
  if (!user) return false;
  const lvl = user.permissions.find((p) => p.module === ModuleKey.DESIGN)?.level ?? PermissionLevel.NONE;
  return hasAtLeast(lvl as PermissionLevel, PermissionLevel.READ);
}
